import React, { useState, useEffect, useMemo } from "react";
import {
  Select,
  MenuItem,
  Box,
  Typography,
  Chip,
  CircularProgress,
  FormControl,
  OutlinedInput,
  TextField,
  ListSubheader,
  InputAdornment,
} from "@mui/material";
import { Icon } from "@iconify/react";
import SearchIcon from "@mui/icons-material/Search";

import { useGetCentres } from "../api/settings";
import TypeIcon from "./Reusables/HubSpokeIcon/TypeIcon";

const CentreSelect = ({
  value,
  valueId,
  onChange,
  placeholder = "Select",
  disabled = false,
  UI_type = "centre",
  autoSelectSingleCentre = false,
  canInterpret,
  menuWidth = "fit-content",
  minMenuWidth = 250,
  maxMenuWidth = 500,

  // NEW:
  // false -> normal CentreSelect behaviour
  // true  -> InterpretationCentreSelect behaviour
  onlyInterpretationCentres = false,
}) => {
  const [selectedCentre, setSelectedCentre] = useState("");
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // Pagination
  const [page, setPage] = useState(1);
  const limit = 50;

  const [allCentres, setAllCentres] = useState([]);
  const [loadingMore, setLoadingMore] = useState(false);

  const { centres, isLoading, pagination } = useGetCentres({
    page,
    limit,
    canInterpret,
  });

  useEffect(() => {
    setPage(1);
    setAllCentres([]);
  }, [canInterpret, onlyInterpretationCentres]);

  useEffect(() => {
    if (!isLoading && Array.isArray(centres)) {
      setAllCentres((prev) => {
        if (page === 1) {
          return [...centres];
        }

        const existingIds = new Set(prev.map((item) => item.id));

        const newItems = centres.filter((item) => !existingIds.has(item.id));

        return [...prev, ...newItems];
      });

      setLoadingMore(false);
    }
  }, [centres, isLoading, page]);

  const handleScroll = (e) => {
    const target = e.currentTarget;

    const isNearBottom =
      target.scrollHeight - target.scrollTop - target.clientHeight < 50;

    const hasMore = page < (pagination?.totalPages || 1);

    if (isNearBottom && hasMore && !loadingMore && !isLoading) {
      setLoadingMore(true);

      setPage((prev) => prev + 1);
    }
  };

  /*
   * -------------------------------------------------------
   * Build Hub -> Spoke Tree
   *
   * NORMAL MODE:
   * Show every hub and every spoke.
   *
   * INTERPRETATION MODE:
   * - Hub selectable only when canInterpretReport === true
   * - Only spokes with canInterpretReport === true
   * - Keep non-interpreting hub as header when it has
   *   interpreting spokes.
   * -------------------------------------------------------
   */
  const treeData = useMemo(() => {
    if (!Array.isArray(allCentres)) {
      return [];
    }

    return allCentres.reduce((acc, centre) => {
      const allSpokes = Array.isArray(centre.spokeCenters)
        ? centre.spokeCenters
        : [];

      const spokes = onlyInterpretationCentres
        ? allSpokes.filter((spoke) => spoke?.canInterpretReport)
        : allSpokes;

      const hubSelectable = onlyInterpretationCentres
        ? !!centre.canInterpretReport
        : true;

      if (onlyInterpretationCentres && !hubSelectable && spokes.length === 0) {
        return acc;
      }

      acc.push({
        value: centre.id,
        label: centre.name || "Unnamed Centre",
        type: centre.centerType,

        isParent: true,
        selectable: hubSelectable,

        isAbhaVerified: centre.isAbhaVerified,

        spokes: spokes.map((spoke) => ({
          value: spoke.id,
          label: spoke.name || "Unnamed Spoke",
          type: spoke.centerType,

          parentId: centre.id,
          parentName: centre.name,

          isChild: true,
          selectable: true,

          isAbhaVerified: spoke.isAbhaVerified,
        })),
      });

      return acc;
    }, []);
  }, [allCentres, onlyInterpretationCentres]);

  const flattenTree = (tree) =>
    tree.flatMap((hub) => {
      const { spokes, ...parent } = hub;

      return [parent, ...spokes];
    });

  const centreOptions = useMemo(() => {
    const options = flattenTree(treeData);

    if (UI_type === "autoSendTo") {
      options.unshift({
        value: "spandan-expert",
        label: "Spandan Doctor",
        type: "expert",

        isExpert: true,
        selectable: true,
      });
    }

    return options;
  }, [treeData, UI_type]);

  /* Search */
  const filteredOptions = useMemo(() => {
    if (!searchTerm.trim()) {
      return centreOptions;
    }

    const lowerSearch = searchTerm.trim().toLowerCase();

    if (!onlyInterpretationCentres) {
      return centreOptions.filter((option) =>
        option.label?.toLowerCase().includes(lowerSearch),
      );
    }

    const filteredTree = treeData.reduce((acc, hub) => {
      const hubMatch = hub.label?.toLowerCase().includes(lowerSearch);

      const matchedSpokes = hub.spokes.filter((spoke) =>
        spoke.label?.toLowerCase().includes(lowerSearch),
      );

      if (hubMatch || matchedSpokes.length > 0) {
        acc.push({
          ...hub,

          spokes: hubMatch ? hub.spokes : matchedSpokes,
        });
      }

      return acc;
    }, []);

    const options = flattenTree(filteredTree);

    /* Search Spandan Doctor */
    if (UI_type === "autoSendTo" && "spandan doctor".includes(lowerSearch)) {
      options.unshift({
        value: "spandan-expert",
        label: "Spandan Doctor",
        type: "expert",

        isExpert: true,
        selectable: true,
      });
    }

    return options;
  }, [centreOptions, treeData, searchTerm, UI_type, onlyInterpretationCentres]);

  /*Auto Select Single Centre */
  useEffect(() => {
    if (isLoading || !autoSelectSingleCentre || selectedCentre || searchTerm) {
      return;
    }

    const availableOptions = onlyInterpretationCentres
      ? centreOptions.filter((option) => option.selectable)
      : centreOptions;

    if (availableOptions.length === 1) {
      const onlyOption = availableOptions[0];

      setSelectedCentre(onlyOption.value);

      onChange?.(onlyOption || null, { auto: true });
    }
  }, [
    isLoading,
    autoSelectSingleCentre,
    centreOptions,
    selectedCentre,
    onChange,
    searchTerm,
    onlyInterpretationCentres,
  ]);

  useEffect(() => {
    if (valueId) {
      setSelectedCentre(valueId);
    } else if (value?.value) {
      setSelectedCentre(value.value);
    }
  }, [valueId, value]);

  const handleChange = (event) => {
    const newValue = event.target.value;

    const selectedObj = centreOptions.find(
      (option) => option.value === newValue,
    );

    if (onlyInterpretationCentres && selectedObj && !selectedObj.selectable) {
      return;
    }

    setSelectedCentre(newValue);

    onChange?.(selectedObj || null, { auto: false });
  };

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSearchTerm("");
  };

  return (
    <Box sx={{ width: "100%" }}>
      <FormControl fullWidth variant="outlined">
        <Select
          open={open}
          onOpen={handleOpen}
          onClose={handleClose}
          value={selectedCentre || ""}
          onChange={handleChange}
          displayEmpty
          disabled={disabled}
          input={
            <OutlinedInput
              notched={false}
              sx={{
                height: "48px",
                borderRadius: "8px",
                backgroundColor: "var(--app-surface)",
                px: 1.25,

                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "var(--app-border)",
                },
              }}
            />
          }
          IconComponent={() =>
            isLoading ? (
              <CircularProgress
                size={18}
                sx={{
                  color: "#5751E9",
                  mr: 2,
                }}
              />
            ) : (
              <Icon
                icon="eva:arrow-ios-downward-fill"
                width="20"
                height="20"
                style={{
                  color: "var(--app-text-muted)",
                  marginLeft: "auto",
                }}
              />
            )
          }
          renderValue={(selected) => {
            if (!selected) {
              return (
                <Typography
                  sx={{
                    color: "var(--app-text-muted)",
                    fontSize: 14,
                  }}
                >
                  {placeholder}
                </Typography>
              );
            }

            const option = centreOptions.find(
              (item) => item.value === selected,
            );

            return (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                {!option?.isExpert && <TypeIcon type={option?.type} />}

                <Typography
                  sx={{
                    color: "var(--app-text)",

                    fontWeight:
                      option?.isParent || option?.isExpert ? 500 : 400,

                    fontSize: 15,
                  }}
                >
                  {option?.label}
                </Typography>
              </Box>
            );
          }}
          MenuProps={{
            autoFocus: false,
            disableAutoFocusItem: true,

            PaperProps: {
              sx: {
                mt: 1,

                borderRadius: "10px",

                boxShadow: "var(--app-shadow)",

                width: menuWidth,
                minWidth: minMenuWidth,
                maxWidth: maxMenuWidth,
              },
            },

            MenuListProps: {
              onScroll: handleScroll,

              sx: {
                maxHeight: 300,
                overflowY: "auto",
              },
            },
          }}
        >
          {/* Search */}

          <ListSubheader>
            <TextField
              size="small"
              autoFocus
              fullWidth
              placeholder="Search centres..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => e.stopPropagation()}
              sx={{
                "& .MuiInputBase-root": {
                  borderRadius: "12px",

                  "& fieldset": {
                    borderColor: "var(--app-border)",
                  },

                  "&:hover fieldset": {
                    borderColor: "var(--app-border)",
                  },

                  "&.Mui-focused fieldset": {
                    borderColor: "var(--app-border)",

                    borderWidth: "1px",
                  },

                  "&.Mui-focused": {
                    boxShadow: "none",
                  },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon
                      sx={{
                        color: "var(--app-text-muted)",
                      }}
                    />
                  </InputAdornment>
                ),
              }}
            />
          </ListSubheader>

          {filteredOptions.map((option) => {
            const isDisabled = onlyInterpretationCentres && !option.selectable;

            return (
              <MenuItem
                key={option.value}
                value={option.value}
                disabled={isDisabled}
                sx={{
                  pl: option.isChild ? 4 : 2,

                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  py: 1.2,

                  ...(isDisabled
                    ? {
                        opacity: 1,
                        cursor: "default",
                        pointerEvents: "none",
                      }
                    : {}),
                }}
              >
                {!option.isExpert && <TypeIcon type={option.type} />}

                <Typography
                  sx={{
                    fontWeight: option.isParent || option.isExpert ? 600 : 400,

                    color: isDisabled
                      ? "var(--app-text-muted)"
                      : "var(--app-text)",

                    fontSize: 14,
                  }}
                >
                  {option.label}
                </Typography>

                {option.isAbhaVerified && (
                  <Chip
                    label="ABHA Verified"
                    size="small"
                    sx={{
                      borderRadius: "8px",
                      ml: "auto",

                      backgroundColor: "#E6F4EA",

                      color: "#2E7D32",

                      fontWeight: 600,
                      fontSize: "12px",
                      height: "22px",
                    }}
                  />
                )}
              </MenuItem>
            );
          })}

          {onlyInterpretationCentres &&
            !isLoading &&
            filteredOptions.length === 0 && (
              <Box textAlign="center" py={2}>
                <Typography
                  fontSize={13}
                  sx={{
                    color: "var(--app-text-muted)",
                  }}
                >
                  No centres available for interpretation
                </Typography>
              </Box>
            )}

          {/* Loading More */}

          {loadingMore && (
            <Box textAlign="center" py={1}>
              <Typography fontSize={12}>Loading more centres...</Typography>
            </Box>
          )}
        </Select>
      </FormControl>
    </Box>
  );
};

export default CentreSelect;
